import { useCallback, useEffect, useRef, useState } from 'react';
import { timerocketApi } from '../services/timerocketApi';
import type {
    AbsencesResponse, HoursListResponse, HoursScope, HoursView, HoursViewer, Period, PeriodPreset, VacationPlanResponse,
} from '../types/timerocket';

/**
 * State for /timerocket-hours: the scope tab, the view tab, the period and the three responses.
 *
 * ONE source of truth for scope and period: the three views (Hours · Absences · Vacation plan)
 * share them, so switching Absences → Hours shows the same people over the same days. Only the
 * view that is on screen is fetched; the other two keep their last answer, which may be for an
 * older period — the effect re-fetches when the user comes back, keyed on scope + period + view.
 *
 * DATES ARE LOCAL CALENDAR DATES. Every key is built from getFullYear/getMonth/getDate — never
 * `toISOString()`, which shifts a CET midnight into the previous UTC day and would make "this
 * month" start on the 31st of last month (feedback_toISOString_timezone). The server works in
 * Europe/Zurich too, so the two agree on what "today" is for everyone in the group.
 *
 * A 403 is an ANSWER, not a failure: the server's reason ("The team view is not enabled yet.")
 * is kept as a notice, the scope falls back to `own` (the one that always answers) and nothing
 * retries — the effect only re-runs when scope, view or period change, so a denied scope cannot
 * loop. ONLY a 403 demotes the scope. A 400 (a too-long custom range), a 500 or a network error
 * keeps the scope and shows the message as `error`: otherwise HR would land in "My hours" after
 * a plain date typo and might not notice that the table now shows one person. The previous
 * response is kept so the scope tabs stay on screen and the user can pick another one.
 */

function dateKey(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseKey(key: string): Date {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d);
}

function minKey(a: string, b: string): string {
    return a < b ? a : b;
}

function daysBetween(from: string, to: string): number {
    return Math.round((parseKey(to).getTime() - parseKey(from).getTime()) / 86_400_000);
}

/**
 * The period a preset means, anchored on `anchor` (any date inside it). Past periods run to
 * their natural end; the one containing today stops AT today, so the target hours shown are
 * the ones that have actually fallen due — a full month's target against four days of actuals
 * would read as a huge deficit on the 4th.
 */
export function periodFor(preset: Exclude<PeriodPreset, 'custom'>, anchor: Date, today: string): Period {
    const y = anchor.getFullYear();
    const m = anchor.getMonth();
    let from: Date;
    let end: Date;
    if (preset === 'month') {
        from = new Date(y, m, 1);
        end = new Date(y, m + 1, 0);
    } else if (preset === 'quarter') {
        const q0 = m - (m % 3);
        from = new Date(y, q0, 1);
        end = new Date(y, q0 + 3, 0);
    } else {
        from = new Date(y, 0, 1);
        end = new Date(y, 11, 31);
    }
    const fromKey = dateKey(from);
    const endKey = dateKey(end);
    // A period wholly in the future keeps its natural end so from <= to still holds; the
    // server answers it with zero rows rather than a 400.
    const to = fromKey > today ? endKey : minKey(endKey, today);
    return { preset, from: fromKey, to };
}

export function useTimeRocketHours(enabled: boolean) {
    const todayKey = dateKey(new Date());
    const [scope, setScopeState] = useState<HoursScope>('own');
    const [view, setViewState] = useState<HoursView>('hours');
    const [period, setPeriod] = useState<Period>(() => periodFor('month', new Date(), todayKey));
    const [data, setData] = useState<HoursListResponse | null>(null);
    const [absences, setAbsences] = useState<AbsencesResponse | null>(null);
    const [vacation, setVacation] = useState<VacationPlanResponse | null>(null);
    /**
     * The viewer from the LAST successful response of any view. The scope tabs mirror it, so
     * they stay on screen when the current view's request is denied or still loading.
     */
    const [viewer, setViewer] = useState<HoursViewer | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /**
     * A denied scope's reason, kept on screen AFTER the fall-back to `own` has answered (the
     * error itself is cleared when the next request starts). Cleared by the next explicit user
     * action — a scope click, a view click, a period change — not by the fall-back's own success.
     */
    const [notice, setNotice] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    const requestSeq = useRef(0);

    // The vacation plan has no period ("today to year end" is the server's), so a period
    // change while that view is open must not fire a request — the key hides the period there.
    const periodKey = view === 'vacation' ? '' : `${period.from}|${period.to}`;

    useEffect(() => {
        if (!enabled) return;
        const seq = ++requestSeq.current;
        setLoading(true);
        setError(null);
        const request: Promise<void> = view === 'hours'
            ? timerocketApi.list({ from: period.from, to: period.to, scope }).then(r => {
                if (seq !== requestSeq.current) return;
                setData(r); setViewer(r.viewer);
            })
            : view === 'absences'
                ? timerocketApi.absences({ from: period.from, to: period.to, scope }).then(r => {
                    if (seq !== requestSeq.current) return;
                    setAbsences(r); setViewer(r.viewer);
                })
                : timerocketApi.vacationPlan({ scope }).then(r => {
                    if (seq !== requestSeq.current) return;
                    setVacation(r); setViewer(r.viewer);
                });
        request
            .catch(err => {
                if (seq !== requestSeq.current) return;
                const message = err instanceof Error ? err.message : 'Failed to load TimeRocket data';
                // A denied `team` / `all` (rights changed under the user, team view switched off) is
                // an ANSWER: keep the server's reason as a notice and fall back to the scope that
                // always answers — once, because the effect keys on scope, so nothing loops. The
                // STATUS decides, never the message text: a 400 or a 500 on the team scope is a
                // failure of this request, not a verdict on the scope, so the scope stays.
                const status = (err as { status?: number }).status;
                if (status === 403 && scope !== 'own') {
                    setNotice(message);
                    setScopeState('own');
                    return;
                }
                setError(message);
            })
            .finally(() => {
                if (seq === requestSeq.current) setLoading(false);
            });
        // periodKey stands in for period.from/to on purpose (see above); the closure reads the
        // period only when periodKey carries it.
    }, [enabled, scope, view, periodKey, reloadKey]);

    const setScope = useCallback((next: HoursScope) => { setNotice(null); setScopeState(next); }, []);
    const setView = useCallback((next: HoursView) => { setNotice(null); setViewState(next); }, []);

    const setPreset = useCallback((preset: PeriodPreset) => {
        setNotice(null);
        setPeriod(prev => {
            if (preset === 'custom') return { ...prev, preset: 'custom' };
            // Re-anchor on the current period's start, so switching Month → Quarter while
            // looking at March 2025 lands on Q1 2025, not on today's quarter.
            return periodFor(preset, parseKey(prev.from), dateKey(new Date()));
        });
    }, []);

    /** Previous / next month, quarter or year; for a custom range, the same length of days. */
    const shiftPeriod = useCallback((direction: 1 | -1) => {
        setNotice(null);
        setPeriod(prev => {
            const today = dateKey(new Date());
            const start = parseKey(prev.from);
            if (prev.preset === 'month') {
                return periodFor('month', new Date(start.getFullYear(), start.getMonth() + direction, 1), today);
            }
            if (prev.preset === 'quarter') {
                return periodFor('quarter', new Date(start.getFullYear(), start.getMonth() + 3 * direction, 1), today);
            }
            if (prev.preset === 'year') {
                return periodFor('year', new Date(start.getFullYear() + direction, 0, 1), today);
            }
            const length = daysBetween(prev.from, prev.to) + 1;
            const from = new Date(start.getFullYear(), start.getMonth(), start.getDate() + direction * length);
            const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + length - 1);
            return { preset: 'custom', from: dateKey(from), to: dateKey(to) };
        });
    }, []);

    /** Both bounds must parse; an inverted range is swallowed silently by keeping the old one. */
    const setCustom = useCallback((from: string, to: string) => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return;
        setNotice(null);
        setPeriod(prev => (from > to ? { ...prev, preset: 'custom' } : { preset: 'custom', from, to }));
    }, []);

    const reload = useCallback(() => { setNotice(null); setReloadKey(k => k + 1); }, []);

    // "Next" makes no sense once the period already reaches today: it would only ask the
    // server for a period with nothing in it.
    const canShiftForward = period.to < todayKey;

    return {
        scope, setScope,
        view, setView,
        period, setPreset, shiftPeriod, setCustom, canShiftForward,
        data, absences, vacation, viewer,
        loading, error, notice, reload,
    };
}
